import React, { useState, useMemo, useEffect } from 'react';
import './Hero.scss';

import { MultiValue, SingleValue } from 'react-select';
import makeAnimated from 'react-select/animated';
import { useLocation, useNavigate } from 'react-router-dom';
import { HeroProps, TagOption } from '../../types';

import pageData from '@/data/pageData.json';

const Hero = ({
	type,
	entityData,
	events,
	setEvents,
	setFilteredEntityData,
	isListingPage = false,
}: HeroProps) => {
	const [chosenServices, setChosenServices] = useState<MultiValue<TagOption>>([]);
	const [selectedLocation, setSelectedLocation] = useState('');
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [selectedProvider, setSelectedProvider] = useState<SingleValue<TagOption>>(null);
	const [, setSelectedDateSort] = useState<SingleValue<TagOption>>(null);
	const [formOrder, setFormOrder] = useState(
		type === 'group-testing' ? 'dateLocation' : 'serviceLocation'
	);
	const animatedComponents = makeAnimated();
	const location = useLocation();
	const navigate = useNavigate();

	const tagButtons = [
		'Diagnostic Tests',
		'Physiotherapy',
		'Osteopathy',
		'Nutrition',
		'Vaccinations',
		'Occupational Health',
		'Orthopaedic',
		'Bariatric',
		'Plastic Surgery',
		'Fertility',
		'Counsellor',
		'Psychologist',
		'Psychotherapist',
		'Wellness Treatment',
	];

	const currentPageData = pageData.find((page) => page.type === type) || null;

	const specialistTagButtons = [
		'Cancer/Oncology',
		'Bowel',
		'Breast',
		'Genetics',
		'Gynaecology',
		'Head neck and thyroid',
		'Lung',
		'Cardiology',
		'Endocrinology',
		'Mental Health',
		'Counsellor',
		'Psychologist',
		'Neurology/Neurosurgery',
	];

	useEffect(() => {
		// If a user removes their chosen services, reset to show the full data
		if (!chosenServices.length) {
			setFilteredEntityData(entityData);
		}
	}, [chosenServices]);

	const availableTags = useMemo(() => {
		const allTags = new Set(
			entityData.flatMap((entity) => entity.entity_with_services.booking_entity.tags.split(', '))
		);
		return Array.from(allTags)
			.filter((tag) =>
				type === 'specialists' ? specialistTagButtons.includes(tag) : tagButtons.includes(tag)
			)
			.sort((a, b) =>
				type === 'specialists'
					? specialistTagButtons.indexOf(a) - specialistTagButtons.indexOf(b)
					: tagButtons.indexOf(a) - tagButtons.indexOf(b)
			);
	}, [entityData, type]);

	const handleTagClick = (tag: string) => {
		const updatedTags = selectedTags.includes(tag)
			? selectedTags.filter((t) => t !== tag)
			: [...selectedTags, tag];

		setSelectedTags(updatedTags);

		const filteredEntityData = entityData.filter((entity) => {
			const entityTags = entity.entity_with_services.booking_entity.tags.split(', ');
			return updatedTags.every((tag) => entityTags.includes(tag));
		});

		setFilteredEntityData(filteredEntityData);

		if (!isListingPage) {
			const queryParams = new URLSearchParams();

			if (chosenServices && chosenServices.length > 0) {
				const serviceNames = chosenServices.map((service) => service.value);
				queryParams.set('services', serviceNames.join(','));
			}

			if (updatedTags.length > 0) {
				queryParams.set('tags', updatedTags.join(','));
			}

			if (selectedLocation) {
				queryParams.set('location', selectedLocation);
			}

			if (selectedProvider) {
				queryParams.set('provider', selectedProvider.value);
			}

			const queryString = queryParams.toString();
			const ListingsPageUrl = currentPageData
				? `/${currentPageData.type}${queryString ? `?${queryString}` : ''}`
				: '';
			navigate(ListingsPageUrl);
		}
	};

	const uniqueServices = new Set<string>();

	const servicesOptions: TagOption[] = entityData
		.flatMap((entity) => {
			const entityTags = entity.entity_with_services.booking_entity.tags || '';

			// Check if the entity matches the current type
			const matchesType =
				(type === 'wellbeing' && entityTags.includes('Wellbeing')) ||
				(type === 'mental-health' && entityTags.includes('Mental Health')) ||
				(type === 'international-treatments' && entityTags.includes('International Treatments')) ||
				(type === 'imaging' && entityTags.includes('Imaging')) ||
				(type !== 'wellbeing' && type !== 'mental-health' && type !== 'international-treatments');

			if (!matchesType || !entity.entity_with_services.available_services) {
				return [];
			}

			const filteredServices = entity.entity_with_services.available_services
				.filter((service: { service: { service_name: string } }) => service?.service)
				.map((service: { service: { service_name: string } }) => ({
					value: service.service?.service_name || '',
					label: service.service?.service_name || '',
				}))
				.filter((service: { value: string }) => {
					if (!uniqueServices.has(service.value)) {
						uniqueServices.add(service.value);
						return true;
					}
					return false;
				});
			return filteredServices;
		})
		.sort((a, b) => a.value.localeCompare(b.value));

	const specialistsOptions = [
		{
			label: 'Cancer/Oncology',
			options: [
				{ value: 'Bowel', label: 'Bowel' },
				{ value: 'Breast', label: 'Breast' },
				{ value: 'Genetics', label: 'Genetics' },
				{ value: 'Gynaecology', label: 'Gynaecology' },
				{ value: 'Head neck and thyroid', label: 'Head neck and thyroid' },
				{ value: 'Lung', label: 'Lung' },
			],
		},
		{
			label: 'Cardiology',
			options: [{ value: 'Cardiology', label: 'Cardiology' }],
		},
		{
			label: 'Endocrinology',
			options: [{ value: 'Endocrinology', label: 'Endocrinology' }],
		},
		{
			label: 'Mental Health',
			options: [
				{ value: 'Counsellor', label: 'Counsellor' },
				{ value: 'Psychologist', label: 'Psychologist' },
			],
		},
		{
			label: 'Neurology/Neurosurgery',
			options: [{ value: 'Neurology/Neurosurgery', label: 'Neurology/Neurosurgery' }],
		},
	];

	const [providerOptions, setProviderOptions] = useState<TagOption[]>([]);

	useEffect(() => {
		if (events && providerOptions.length === 0) {
			const uniqueProviders = Array.from(new Set(events.map((event) => event.event_organiser)));
			setProviderOptions(
				uniqueProviders.map((provider) => ({
					value: provider,
					label: provider,
				}))
			);
		}
	}, [events]); // Only run on mount, and do nothing if `providerOptions` is already set

	type Service = {
		service_name: string;
	};

	type AvailableService = {
		service: Service;
	};

	type EntityWithServices = {
		booking_entity: {
			tags: string;
			latitude: number;
			longitude: number;
		};
		available_services: AvailableService[];
	};

	type EntityData = {
		entity_with_services: EntityWithServices;
	};

	useEffect(() => {
		if (isListingPage) {
			const queryParams = new URLSearchParams(location.search);
			const servicesParam = queryParams.get('services')?.split(',');
			const tagsParam = queryParams.get('tags')?.split(',');
			const locationParam = queryParams.get('location');
			const providerParam = queryParams.get('provider');
			const dateSortParam = queryParams.get('dateSort');

			if (servicesParam && servicesParam.length > 0) {
				const initialServices = servicesParam.map((service) => ({
					value: service,
					label: service,
				}));
				setChosenServices(initialServices);
			}

			setSelectedTags(tagsParam || []);
			setSelectedLocation(locationParam || '');
			setSelectedProvider(providerParam ? { value: providerParam, label: providerParam } : null);
			setSelectedDateSort(
				dateSortParam
					? {
							value: dateSortParam,
							label: dateSortParam === 'soonest' ? 'Soonest date' : 'Furthest date',
						}
					: null
			);
		}
	}, [location.search, isListingPage]);

	useEffect(() => {
		setSelectedTags([]);
		setChosenServices([]);
		setSelectedLocation('');
		setSelectedProvider(null);
		setSelectedDateSort(null);
	}, []);

	return (
		<div className="hero-bg">
			<div className="container">
				<div className="row py-3 py-lg-5 pb-2 pb-lg-5 flex-column-reverse flex-lg-row">
					<div className="col-lg-12 mx-auto align-self-center">
						<div className="pt-4 pt-lg-0 ps-lg-3">
							{currentPageData && <h3>{currentPageData.heroTitle}</h3>}
							{currentPageData?.heroParagraphs &&
								currentPageData.heroParagraphs.map((paragraph, index) => (
									<p key={index}>{paragraph}</p>
								))}
							{currentPageData?.heroTitle === 'International Treatments' && (
								<p>
									If you are unsure of anything, please{' '}
									<a
										href="https://forms.office.com/e/5K9t3bSDBG"
										target="_blank"
										rel="noopener noreferrer"
									>
										<u>contact us</u>
									</a>{' '}
									and we will respond within 1 business day.
								</p>
							)}
							{currentPageData?.heroTitle === 'Imaging' && (
								<p>
									If you are unsure of anything, please{' '}
									<a
										href="https://forms.office.com/e/z2KGAPqGmn"
										target="_blank"
										rel="noopener noreferrer"
									>
										<u>contact us</u>
									</a>{' '}
									and we will respond within 1 business day.
								</p>
							)}
						</div>
					</div>
				</div>
				{isListingPage ? (
					<div className="pb-4">
						{availableTags.map((tag) => (
							<button
								key={tag}
								className={`hero-tag-btn ${selectedTags.includes(tag) ? 'selected' : ''}`}
								type="button"
								onClick={() => handleTagClick(tag)}
							>
								{tag}
							</button>
						))}
					</div>
				) : (
					<div className="pb-1"></div>
				)}
			</div>
		</div>
	);
};

export default Hero;
